import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Popconfirm } from '@/components/Popconfirm';
import { Button, Input, Text } from '@/components/ui';
import { SaveIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useProfile, ProfileFormData } from '@/hooks/useProfile';
import { Loader } from '@/components/Loader';

const profileSchema = (t: any) =>
  z.object({
    name: z.string().min(1, t('users.nameRequired')),
    email: z.string().email(t('auth.invalidEmail')),
    password: z.string().optional().or(z.string().length(0)),
  });

type ProfileForm = z.infer<ReturnType<typeof profileSchema>>;

export function MePage() {
  const { t } = useTranslation();
  const { isLoading, isSaving, getFormData, saveProfile, deleteAccount } = useProfile();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema(t)),
    defaultValues: {
      name: '',
      email: '',
      password: '',
    },
  });

  useEffect(() => {
    if (!isLoading) {
      const formData = getFormData();
      reset(formData);
    }
  }, [isLoading, getFormData, reset]);

  async function onSubmit(data: ProfileForm) {
    await saveProfile(data as ProfileFormData);
  }

  async function handleDeleteAccount() {
    await deleteAccount();
  }

  return (
    <div className="gap-2xl flex flex-1 flex-col">
      <Loader isLoading={isLoading} />
      <header className="flex h-fit w-full items-center justify-between">
        <Text as="h1" variant="heading">
          {t('me.title')}
        </Text>
        <div>
          <Button onClick={handleSubmit(onSubmit)} loading={isSaving} disabled={isLoading}>
            <SaveIcon size={20} />
            {t('common.save')}
          </Button>
        </div>
      </header>

      <section className="gap-md grid grid-cols-2">
        <Input
          {...register('name')}
          label={t('me.form.name')}
          placeholder={t('me.form.namePlaceholder')}
          error={errors.name?.message}
          disabled={isLoading}
        />
        <Input
          {...register('email')}
          type="email"
          label={t('me.form.email')}
          placeholder={t('me.form.emailPlaceholder')}
          error={errors.email?.message}
          disabled={isLoading}
        />
        <Input
          {...register('password')}
          type="password"
          label={t('me.form.password')}
          placeholder={t('me.form.passwordPlaceholder')}
          error={errors.password?.message}
          disabled={isLoading}
        />
        <div className="flex items-end">
          <Text className="text-neutral-muted">
            <Trans i18nKey="me.form.passwordInfo" components={{ break: <br /> }} />
          </Text>
        </div>
      </section>

      <section>
        <Popconfirm
          title={t('me.deleteAction.title')}
          description={t('me.deleteAction.description')}
          confirmLabel={t('me.deleteAction.label')}
          onConfirm={handleDeleteAccount}
          variant="danger"
        >
          <button
            className="outline-error-subtle rounded-xs focus:outline-2 focus:outline-offset-2"
            disabled={isLoading}
          >
            <Text className="text-error-default hover:text-error-muted" strong>
              {t('me.deleteAction.title')}
            </Text>
          </button>
        </Popconfirm>
      </section>
    </div>
  );
}
